/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useMemo } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { KpiCardGrid } from "../kpi_card_grid";
import { ModalTable } from "../modal_table";
import { StatusPill, SummaryAlert, SummaryAlertText, SummarySection } from "./styles.jsx";

const SUMMARY_ICON_BACKGROUND = "#FFF7ED";
const SUMMARY_ICON_COLOR = "#EA580C";

const formatField = (value, separator = ", ") => {
  if (Array.isArray(value)) {
    return value.join(separator);
  }

  if (value == null || value === "") {
    return "-";
  }

  return `${value}`;
};

export default function ImportSummaryStep({ result }) {
  const summary = result?.summary || {};
  const groupedRows = result?.rows || [];
  const failedCount = groupedRows.filter((row) => row?.status === "failed").length;

  const cards = useMemo(() => ([
    {
      key: "rows_read",
      title: "Rows Read",
      icon: <TableChartOutlinedIcon sx={{ fontSize: 24, color: SUMMARY_ICON_COLOR }} />,
      value: summary?.rows_read || 0,
      iconBackground: SUMMARY_ICON_BACKGROUND,
    },
    {
      key: "employee_groups",
      title: "Groups Found",
      icon: <GroupsOutlinedIcon sx={{ fontSize: 24, color: SUMMARY_ICON_COLOR }} />,
      value: summary?.employee_groups || 0,
      iconBackground: SUMMARY_ICON_BACKGROUND,
    },
    {
      key: "created",
      title: "Employees Created",
      icon: <HowToRegOutlinedIcon sx={{ fontSize: 24, color: SUMMARY_ICON_COLOR }} />,
      value: summary?.created || 0,
      iconBackground: SUMMARY_ICON_BACKGROUND,
    },
    {
      key: "failed",
      title: "Employees Failed",
      icon: <PersonOffOutlinedIcon sx={{ fontSize: 24, color: SUMMARY_ICON_COLOR }} />,
      value: summary?.failed || 0,
      iconBackground: SUMMARY_ICON_BACKGROUND,
    },
  ]), [summary]);

  const columns = useMemo(() => ([
    { key: "source_rows", label: "Source Rows", sortable: false, width: 160 },
    { key: "status", label: "Status", sortable: false, width: 140 },
    { key: "phone_number", label: "Key Identifier (Phone)", sortable: false, width: 220 },
    { key: "messages", label: "Messages", sortable: false, width: 520 },
  ]), []);

  const rows = useMemo(() => (
    groupedRows.map((row, index) => ({
      id: index + 1,
      source_rows: formatField(row?.rows),
      status: {
        display: <StatusPill size="small" label={row?.status === "created" ? "Created" : "Failed"} statusvalue={row?.status} />,
      },
      phone_number: row?.phone_number || "-",
      messages: row?.status === 'created' && row?.messages?.length === 0 ? 'Record created successfully'  : formatField(row?.messages, " | "),
    }))
  ), [groupedRows]);

  return (
    <SummarySection data-testid="manual-csv-import-summary-step">
      <div>
        <KpiCardGrid
          config={cards.map((card) => ({
            key: card.key,
            title: card.title,
            icon: card.icon,
            getValue: () => card.value,
          }))}
          data={summary}
          loading={false}
          isMobile={false}
          columns={{ xs: 1, sm: 2, md: 2, lg: 4 }}
          borderColor="#e5e7eb"
          containerSx={{
            marginTop: "0px",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            boxShadow: "0px 8px 20px rgba(15, 23, 42, 0.08)",
          }}
          getCardProps={(_item, index) => ({
            sx: {
              minHeight: 96,
              padding: "16px 18px",
              backgroundColor: "#fff",
              boxShadow: "none",
              "& > div > div:first-of-type": {
                backgroundColor: cards[index]?.iconBackground,
              },
            },
          })}
        />
      </div>

      <SummaryAlert data-testid="manual-csv-import-summary-alert" severitytype={failedCount > 0 ? "warning" : "success"}>
        {failedCount > 0 ? (
          <InfoOutlinedIcon sx={{ color: "#d97706" }} />
        ) : (
          <CheckCircleOutlineOutlinedIcon sx={{ color: "#16a34a" }} />
        )}
        <SummaryAlertText>
          {failedCount > 0
            ? `${failedCount} grouped rows have validation errors and will be skipped. Fix them in your CSV and re-upload to include them.`
            : "All grouped rows were processed successfully."}
        </SummaryAlertText>
      </SummaryAlert>

      <ModalTable
        columns={columns}
        data={rows}
        type="employee import rows"
        itemsPerPage={Math.max(rows.length, 1)}
        setItemsPerPage={() => {}}
        page={1}
        setPage={() => {}}
        pageCount={rows.length}
        report={false}
        hideNavigation={true}
      />
    </SummarySection>
  );
}
